import {
  CalendarDots,
  ClockCounterClockwise,
  ListBullets,
} from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import '../discovery.css'

import { KuniriLogo } from '@/components/kuniri-logo'
import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'

import { ActivityGrid } from '../components/activity-grid'
import { AgendaCalendar } from '../components/agenda-calendar'
import { DiscoveryNotice } from '../components/discovery-notice'
import { EmptyState } from '../components/empty-state'
import { PullToRefresh } from '../components/pull-to-refresh'
import { RefreshButton } from '../components/refresh-button'
import { RefreshStatus } from '../components/refresh-status'
import { useActivityList } from '../hooks/use-activity-list'
import { useIsDesktop } from '../hooks/use-is-desktop'
import { useSaveToggle } from '../hooks/use-save-toggle'
import type { Activity } from '../types'

type AgendaView = 'calendar' | 'list'

function startTimestamp(activity: Activity): number {
  if (!activity.startAt) return Number.POSITIVE_INFINITY
  const value = Date.parse(activity.startAt)
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY
}

/**
 * Personal agenda (`/saved`): one list with created, joined, attended, pending
 * and waitlisted activities (D-052 / UI_PARITY §3.5.1). Bookmarks live in
 * `/bookmarks` and history in `/history` (D-043).
 */
export function AgendaScreen() {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const { items, phase, refreshing, error, refresh, setItems } =
    useActivityList('agenda')
  const { pendingIds, notice, dismissNotice, toggleSaved } =
    useSaveToggle(setItems)
  const [view, setView] = useState<AgendaView>('calendar')

  const agendaItems = useMemo(
    () => [...items].sort((a, b) => startTimestamp(a) - startTimestamp(b)),
    [items],
  )

  return (
    <section aria-labelledby="agenda-title" className="content-width pb-10">
      <header className="compact-sticky-header flex items-center gap-2 py-1">
        <Link to="/home" className="mr-auto inline-flex min-h-11 items-center">
          <KuniriLogo height={24} />
        </Link>
        <Link
          to="/history"
          className="inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-sm font-bold text-teal hover:bg-mint"
        >
          <ClockCounterClockwise size={17} weight="bold" aria-hidden="true" />
          {t('screens.history.title')}
        </Link>
        {isDesktop ? (
          <RefreshButton onRefresh={refresh} refreshing={refreshing} />
        ) : null}
      </header>

      <div className="mt-3 flex items-center gap-3">
        <h1 id="agenda-title" className="mr-auto text-[22px] font-extrabold">
          {t('screens.saved.title')}
        </h1>
        <div
          className="inline-flex rounded-full border border-line bg-white p-0.5"
          role="group"
          aria-label={t('discovery.agenda.viewLabel')}
        >
          <button
            type="button"
            className={`view-toggle size-10 justify-center rounded-full px-0 ${
              view === 'calendar' ? 'is-active' : ''
            }`}
            aria-pressed={view === 'calendar'}
            aria-label={t('discovery.agenda.calendar')}
            onClick={() => setView('calendar')}
          >
            <CalendarDots size={19} weight="bold" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`view-toggle size-10 justify-center rounded-full px-0 ${
              view === 'list' ? 'is-active' : ''
            }`}
            aria-pressed={view === 'list'}
            aria-label={t('discovery.agenda.list')}
            onClick={() => setView('list')}
          >
            <ListBullets size={19} weight="bold" aria-hidden="true" />
          </button>
        </div>
      </div>

      <PullToRefresh
        className="mt-4"
        onRefresh={refresh}
        refreshing={refreshing}
        disabled={isDesktop}
      >
        <RefreshStatus visible={refreshing} />
        {phase === 'loading' ? (
          <LoadingState labelKey="discovery.agenda.loading" />
        ) : phase === 'error' ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : agendaItems.length === 0 ? (
          <EmptyState
            icon={<CalendarDots size={28} weight="bold" aria-hidden="true" />}
            titleKey="discovery.agenda.empty.title"
            bodyKey="discovery.agenda.empty.body"
          />
        ) : view === 'calendar' ? (
          <AgendaCalendar activities={agendaItems} />
        ) : (
          <ActivityGrid
            activities={agendaItems}
            onToggleSave={(activity) => void toggleSaved(activity)}
            pendingSaveIds={pendingIds}
          />
        )}
      </PullToRefresh>

      <DiscoveryNotice notice={notice} onDismiss={dismissNotice} />
    </section>
  )
}
